import { useMemo, useState, useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

import { getUsersPaginated } from '../../../../redux/slices/userSlice';

const toActionEntity = (row: any): ActionEntity => ({ type: 'user', id: row.user_id, name: row.username || `User ${row.user_id}` });

const ListUsers = () => {
  const dispatch = useAppDispatch();

  const { users, config: headerConfig, filtersConfig, pagination, error, loadingFlags } = useAppSelector((state) => (state as any).user);

  const userData = useAppSelector((state) => state.auth.userData);

  const isLoading = loadingFlags?.getUsersPaginated ?? false;

  const pageName = 'users';
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    dispatch(getUsersPaginated({}) as any);
  }, [dispatch]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getUsersPaginated,
    getFormattedRow: (row: any) => ({ ...row }),
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'addToTransportUser', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [...baseColumns, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(users || []), [users]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.user_id),
  });

  const { currentPage, totalPages, totalItems } = pagination || {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
  };

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createUser" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportUser" className="ms-2" variant="secondary" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListUsers;
